import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { RouterModule, UrlSegment } from '@angular/router';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { AbstractPane } from '@app/library/panes/models/abstract-pane';
import { CLibraryFilterData } from '@app/store/models';
import { LibraryRouter } from '@models/library.router';
import { Store } from '@ngrx/store';
import { getLibraryRouteData } from '@store/reducers/library.reducer';
import { getMe } from '@store/reducers/profile.reducer';
import { currencies, EContentPanelRoute, User } from 'lingo2-models';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { DeviceDetectorService } from 'ngx-device-detector';
import { takeUntil } from 'rxjs/operators';
import { i18nService as TranslateService } from '../../../i18n/translate.service';

enum ENewPages {
  courses = 'courses',
}

@Component({
  selector: 'app-base-pages',
  standalone: true,
  imports: [LocaleSharedModule, CommonModule, RouterModule, LazyLoadImageModule],
  templateUrl: './base-pages.component.html',
  styleUrls: ['./base-pages.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BasePagesComponent extends AbstractPane implements OnInit {
  translate = inject(TranslateService);
  protected readonly store = inject(Store);

  public basePagesList = [
    EContentPanelRoute.classes,
    EContentPanelRoute.tutors,
    EContentPanelRoute.lessons,
    EContentPanelRoute.collections,
    EContentPanelRoute.schools,
  ];

  private _libraryFilterData: CLibraryFilterData;
  private _me: User;
  private libraryRouter: LibraryRouter = new LibraryRouter();

  public readonly deviceService: DeviceDetectorService;

  ngOnInit(): void {
    this.store
      .select(getLibraryRouteData)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (libraryFilterData) => {
          this._libraryFilterData = libraryFilterData;
          this.libraryRouter.setSubjects(this._libraryFilterData?.subjectCodes);
          this.markForCheck();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this._me = me;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public hasContent(): boolean {
    return !!this._pane;
  }

  public isDisabled(page): boolean {
    return page === ENewPages.courses;
  }

  public url(baseUrl: EContentPanelRoute): UrlSegment {
    this.libraryRouter.setBaseRoute(baseUrl);
    return this.libraryRouter.getLink();
  }

  protected onContentLoaded(viaQuery?: boolean): void {
    /**
     * Так как панель не имеет динамического контента,
     * а всего лишь информационная - по умолчанию всегда отображается
     */
    this.isVisible = true;
  }

  public get getMyCurrency() {
    if (this._me?.currency_id) {
      return this.translate.directInstant(currencies[this._me?.currency_id]?.title);
    } else {
      return this.translate.directInstant('USD');
    }
  }

  public trackByFn(index) {
    return index;
  }
}
